import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	Button,
	Group,
	TextInput,
	Table,
	Paper,
	Title,
	Text,
	Box,
	Modal,
	MediaQuery,
	FileInput,
	Progress,
	List,
	Pagination,
	Loader,
} from '@mantine/core'
import {
	getExperienceCheckIns,
	uploadExperienceCheckIns,
	deleteCheckIn,
} from '../services/actions/checkInActions'
import {
	getStationOptions,
	getItemOptions,
} from '../services/actions/experienceActions'
import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import TicketIcon from 'tabler-icons-react/dist/icons/ticket'

const ManageCheckInsPage = () => {
	const { experienceSlug } = useParams()
	const navigate = useNavigate()
	const experience = useSelector((state) => state.experience?.experienceDetails)

	const [checkIns, setCheckIns] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [showUploadModal, setShowUploadModal] = useState(false)
	const [searchTerm, setSearchTerm] = useState('')
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [checkInToDelete, setCheckInToDelete] = useState(null)
	const [uploadProgress, setUploadProgress] = useState(null)
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(50)

	const detectDevice = () => (window.innerWidth < 768 ? 'mobile' : 'desktop')

	useEffect(() => {
		const size = detectDevice()
		setPageSize(size === 'mobile' ? 20 : 50)
		fetchCheckIns()
	}, [experienceSlug])

	const fetchCheckIns = async () => {
		setLoading(true)
		try {
			const results = await getExperienceCheckIns(experienceSlug)
			setCheckIns(results)
		} catch (err) {
			console.error('Failed to fetch check-ins:', err)
			setError('Failed to load check-ins')
		} finally {
			setLoading(false)
		}
	}

	const handleDownloadCheckIns = () => {
		const sample =
			checkIns.length > 0
				? checkIns
				: [
						{
							id: '',
							travelerName: 'John Doe',
							travelerEmail: 'john@example.com',
							travelerPhone: '555-555-5555',
							accountName: 'Ohio University',
							scheduledCheckInDate: new Date('2025-02-08'),
							stationName: 'Front Desk',
							bookingNumber: 'BOOK-12345',
							notes: '',
							purchasedItems: [{ name: 'Day Pass', issued: false }],
							warnings: [{ title: 'Missing ID', level: 'Warning' }],
							checkInStatus: false,
						},
				  ]

		const formatted = sample.map((c) => ({
			id: c.id,
			travelerName: c.travelerName,
			travelerEmail: c.travelerEmail,
			travelerPhone: c.travelerPhone,
			accountName: c.accountName,
			scheduledCheckInDate: c.scheduledCheckInDate,
			stationName: c.stationName || '',
			bookingNumber: c.bookingNumber,
			notes: c.notes,
			purchasedItems: JSON.stringify(c.purchasedItems || []),
			warnings: JSON.stringify(c.warnings || []),
			checkInStatus: c.checkInStatus ?? false,
		}))

		const csv = Papa.unparse(formatted)
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		saveAs(blob, `${experienceSlug}_checkins.csv`)
	}

	const parsePurchasedItems = (cell, itemMap) => {
		if (!cell) return []
		try {
			const parsed = JSON.parse(cell)
			if (Array.isArray(parsed)) {
				return parsed
					.map((item) => {
						const id = itemMap[item.name?.toLowerCase()]
						return id ? { id, issued: item.issued ?? false } : null
					})
					.filter(Boolean)
			}
		} catch {}

		return cell
			.split(',')
			.map((name) => name.trim().toLowerCase())
			.map((name) => {
				const id = itemMap[name]
				return id ? { id, issued: false } : null
			})
			.filter(Boolean)
	}

	const parseWarnings = (cell) => {
		if (!cell) return []
		try {
			const parsed = JSON.parse(cell)
			if (Array.isArray(parsed)) return parsed
		} catch {}
		return cell.split(',').map((pair) => {
			const [title, level] = pair.split(':')
			return {
				title: title.trim(),
				level: (level || 'General').trim(),
			}
		})
	}

	const handleFileUpload = async (file) => {
		if (!file) return

		try {
			const [stationOptions, itemOptions] = await Promise.all([
				getStationOptions(experienceSlug),
				getItemOptions(experienceSlug),
			])

			const stationMap = Object.fromEntries(
				stationOptions.map((s) => [s.name.toLowerCase(), s.id])
			)

			const itemMap = Object.fromEntries(
				itemOptions.map((i) => [i.name.toLowerCase(), i.id])
			)

			Papa.parse(file, {
				header: true,
				complete: async (results) => {
					try {
						setUploadProgress(10)

						const parsedCheckIns = results.data.map((row, index) => {
							if (index % 10 === 0) {
								setUploadProgress(
									Math.min(80, 10 + (index / results.data.length) * 70)
								)
							}

							return {
								id: row.id,
								travelerName: row.travelerName,
								travelerEmail: row.travelerEmail,
								travelerPhone: row.travelerPhone,
								accountName: row.accountName,
								station:
									stationMap[row.stationName?.trim().toLowerCase()] || '',
								bookingNumber: row.bookingNumber,
								notes: row.notes || '',
								scheduledCheckInDate: row.scheduledCheckInDate
									? new Date(row.scheduledCheckInDate)
									: null,
								purchasedItems: parsePurchasedItems(
									row.purchasedItems,
									itemMap
								),
								warnings: parseWarnings(row.warnings),
								checkInStatus:
									typeof row.checkInStatus === 'string'
										? row.checkInStatus.trim().toLowerCase() === 'true'
										: !!row.checkInStatus,
							}
						})

						setUploadProgress(90)
						await uploadExperienceCheckIns(experienceSlug, parsedCheckIns)
						setUploadProgress(100)

						setTimeout(() => {
							setShowUploadModal(false)
							setUploadProgress(null)
							fetchCheckIns()
						}, 500)
					} catch (err) {
						console.error('Failed to upload check-ins:', err)
						setError('Failed to upload check-ins')
						setUploadProgress(null)
					}
				},
			})
		} catch (err) {
			console.error('❌ Error preparing upload:', err)
			setError('Failed to process station/item data')
		}
	}

	const handleDeleteConfirmed = async () => {
		try {
			await deleteCheckIn(checkInToDelete.id)
			setDeleteModalOpen(false)
			setCheckInToDelete(null)
			await fetchCheckIns()
		} catch (err) {
			console.error('Failed to delete check-in:', err)
			setError('Failed to delete check-in')
		}
	}

	const filteredCheckIns = checkIns.filter((c) => {
		const term = searchTerm.toLowerCase()
		return (
			c.travelerName?.toLowerCase().includes(term) ||
			c.travelerEmail?.toLowerCase().includes(term) ||
			c.travelerPhone?.toLowerCase().includes(term) ||
			c.bookingNumber?.toLowerCase().includes(term)
		)
	})

	const paginatedCheckIns = filteredCheckIns.slice(
		(page - 1) * pageSize,
		page * pageSize
	)
	const totalPages = Math.ceil(filteredCheckIns.length / pageSize)

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Manage Check-Ins
			</Title>

			{error && <Text color='red'>{error}</Text>}

			<Group mb='sm'>
				<Button
					onClick={() =>
						navigate(`/experiences/${experienceSlug}/manage/checkin/new`)
					}
				>
					Add Check-In
				</Button>
				<Button onClick={handleDownloadCheckIns}>Download</Button>
				{experience.allowCheckInMassImport && (
					<Button onClick={() => setShowUploadModal(true)}>Upload</Button>
				)}
			</Group>

			<TextInput
				placeholder='Filter check-ins'
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
				mb='md'
			/>

			{loading ? (
				<Loader />
			) : paginatedCheckIns.length === 0 ? (
				<Text>No check-ins found</Text>
			) : (
				<>
					{totalPages > 1 && (
						<Box mb='sm'>
							<Pagination
								value={page}
								onChange={setPage}
								total={totalPages}
								size='sm'
							/>
						</Box>
					)}
					<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
						<Box>
							{paginatedCheckIns.map((c) => (
								<Paper key={c.id} withBorder p='sm' mb='sm'>
									<Text fw={600}>{c.travelerName}</Text>
									<Text size='xs' c='dimmed'>
										{c.travelerEmail}
									</Text>
									<Text size='xs' c='dimmed'>
										{c.travelerPhone}
									</Text>
									<Text size='xs' c='dimmed'>
										{c.accountName}
									</Text>
									<Text size='xs'>
										Date: {c.scheduledCheckInDate?.slice(0, 10)}
									</Text>
									<Text size='xs'>Station: {c.stationName}</Text>
									<Text size='xs'>Booking: {c.bookingNumber}</Text>
									<Text size='xs'>
										Checked In?: {c.checkInStatus ? '✅ Yes' : '❌ No'}
									</Text>
									<Group mt='xs'>
										<Button
											size='xs'
											style={{ width: 85 }}
											onClick={() =>
												navigate(
													`/experiences/${experienceSlug}/checkIn/${c.id}`
												)
											}
										>
											{c.checkInStatus ? 'View' : 'Check In'}
										</Button>
										<Button
											size='xs'
											onClick={() =>
												navigate(
													`/experiences/${experienceSlug}/manage/checkin/${c.id}`
												)
											}
										>
											Edit
										</Button>
										<Button
											size='xs'
											color='red'
											onClick={() => {
												setCheckInToDelete(c)
												setDeleteModalOpen(true)
											}}
										>
											Delete
										</Button>
										<a
											href={`/get-pass/${c.id}`}
											target='_blank'
											rel='noopener noreferrer'
										>
											<Button size='xs' variant='light'>
												<TicketIcon size={14} />
											</Button>
										</a>
									</Group>
								</Paper>
							))}
						</Box>
					</MediaQuery>

					<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
						<Box>
							<Paper withBorder p='sm'>
								<Table striped highlightOnHover>
									<thead>
										<tr>
											<th>Booking</th>
											<th>Name</th>
											<th>Email</th>
											<th>Checked In?</th>
											<th>Actions</th>
										</tr>
									</thead>
									<tbody>
										{paginatedCheckIns.map((c) => (
											<tr key={c.id}>
												<td>{c.bookingNumber}</td>
												<td>{c.travelerName}</td>
												<td>
													{' '}
													{c.travelerEmail.length > 25
														? `${c.travelerEmail.slice(0, 22)}...`
														: c.travelerEmail}
												</td>
												<td>{c.checkInStatus ? '✅ Yes' : '❌ No'}</td>
												<td>
													<Group>
														<Button
															size='xs'
															style={{ width: 85 }}
															onClick={() =>
																navigate(
																	`/experiences/${experienceSlug}/checkIn/${c.id}`
																)
															}
														>
															{c.checkInStatus ? 'View' : 'Check In'}
														</Button>
														<Button
															size='xs'
															onClick={() =>
																navigate(
																	`/experiences/${experienceSlug}/manage/checkin/${c.id}`
																)
															}
														>
															Edit
														</Button>
														<Button
															size='xs'
															color='red'
															onClick={() => {
																setCheckInToDelete(c)
																setDeleteModalOpen(true)
															}}
														>
															Delete
														</Button>
														<a
															href={`/get-pass/${c.id}`}
															target='_blank'
															rel='noopener noreferrer'
														>
															<Button size='xs' variant='light'>
																<TicketIcon size={14} />
															</Button>
														</a>
													</Group>
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							</Paper>
						</Box>
					</MediaQuery>
				</>
			)}

			<Modal
				opened={showUploadModal}
				onClose={() => {
					if (uploadProgress === null) {
						setShowUploadModal(false)
					}
				}}
				title='Upload Check-Ins'
				withCloseButton={uploadProgress === null}
				closeOnClickOutside={uploadProgress === null}
				closeOnEscape={uploadProgress === null}
			>
				<FileInput
					label='CSV File'
					accept='.csv'
					onChange={handleFileUpload}
					placeholder='Select a .csv file'
					withAsterisk
					mt='md'
					disabled={uploadProgress !== null}
				/>

				{uploadProgress !== null && (
					<Box mt='md'>
						<Text size='sm' mb='xs'>
							Uploading...
						</Text>
						<Progress value={uploadProgress} striped animated />
					</Box>
				)}

				<Box mt='lg'>
					<Text size='sm' fw={500}>
						Instructions:
					</Text>
					<List withPadding size='sm' mt='xs'>
						<li>
							You can upload full JSON or comma-separated values for{' '}
							<code>purchasedItems</code> and <code>warnings</code>.
						</li>
						<li>
							Example Purchased Items: <code>Party Pass, Sunset Cruise</code> or
							JSON.
						</li>
						<li>
							Example Warnings: <code>Missing ID:Warning, Unpaid:Critical</code>{' '}
							or JSON.
						</li>
						<li>Notes field is optional.</li>
					</List>
				</Box>
			</Modal>

			<Modal
				opened={deleteModalOpen}
				onClose={() => setDeleteModalOpen(false)}
				title='Confirm Deletion'
				centered
			>
				<Text mb='md'>
					Are you sure you want to permanently delete{' '}
					<strong>{checkInToDelete?.travelerName}</strong>'s check-in?
				</Text>
				<Group position='right'>
					<Button variant='light' onClick={() => setDeleteModalOpen(false)}>
						Cancel
					</Button>
					<Button color='red' onClick={handleDeleteConfirmed}>
						Permanently Delete
					</Button>
				</Group>
			</Modal>
		</Box>
	)
}

export default ManageCheckInsPage
